import {
    faArrowRight,
    faLocationArrow,
    faCircleCheck,
    faListUl,
    faRoute,
    faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import Helper from "../../../Helper";
import { GateToDropoffRoute } from "../../../models/GateToDropoffRoute";
import { WaypointStep } from "../../../models/WaypointStep";
import { convertDistanceToReadable } from "../../Helper/WaypointGuidance";
import WaypointManeuverIcon from "./WaypointManeuverIcon";

interface WaypointGuidanceBottomBarProps {
    steps: WaypointStep[];
    activeStep: number;
    setActiveStep: (step: number) => void;
    remainingMeters: number;
    hasUserLocation: boolean;
    toggleLocation: () => void;
    arrived: boolean;
    destinationName?: string | null;
    // Routes to offer once this one is finished, instead of ending guidance.
    continuationRoutes: GateToDropoffRoute[];
    continueOnto: (route: GateToDropoffRoute) => void;
    exit: () => void;
}

export default function WaypointGuidanceBottomBar({
    steps,
    activeStep,
    setActiveStep,
    remainingMeters,
    hasUserLocation,
    toggleLocation,
    arrived,
    destinationName,
    continuationRoutes,
    continueOnto,
    exit,
}: WaypointGuidanceBottomBarProps) {
    const [stepListOpen, setStepListOpen] = useState(false);
    const stepsLeft = steps.length - activeStep + 1;

    const jumpToStep = (step: number) => {
        setActiveStep(step);
        setStepListOpen(false);
    };

    if (arrived && continuationRoutes.length > 0) {
        return (
            <div className="z-10 bg-white rounded-t-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.12)] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-4">
                <div className="flex items-center gap-4">
                    <FontAwesomeIcon icon={faRoute} className="text-plot-orange text-3xl" />
                    <div className="flex-1 min-w-0">
                        <div className="text-xl font-bold text-plot-slate">Haul route complete</div>
                        <div className="text-sm text-gray-500">
                            Continue with directions from the gate to the dropoff
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    {continuationRoutes.map((route) => (
                        <button
                            key={route.id}
                            type="button"
                            onClick={() => continueOnto(route)}
                            className="cursor-pointer w-full flex items-center justify-center gap-2 rounded-full bg-plot-orange hover:brightness-95 text-white font-semibold px-6 py-3"
                        >
                            {continuationRoutes.length === 1 ? "Continue" : route.name || "Gate-to-Dropoff Route"}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={exit}
                        className="cursor-pointer w-full rounded-full border border-gray-200 text-plot-slate font-semibold px-6 py-3 hover:bg-gray-50"
                    >
                        End directions
                    </button>
                </div>
            </div>
        );
    }

    if (arrived) {
        return (
            <div className="z-10 bg-white rounded-t-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.12)] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex items-center gap-4">
                <FontAwesomeIcon icon={faCircleCheck} className="text-plot-orange text-4xl" />
                <div className="flex-1 min-w-0">
                    <div className="text-xl font-bold text-plot-slate">You've arrived</div>
                    {destinationName && <div className="text-sm text-gray-500 truncate">{destinationName}</div>}
                </div>
                <button
                    type="button"
                    onClick={exit}
                    className="cursor-pointer rounded-full bg-plot-orange hover:brightness-95 text-white font-semibold px-6 py-3"
                >
                    Done
                </button>
            </div>
        );
    }

    return (
        <div className="relative z-10">
            {stepListOpen && (
                <ol className="absolute bottom-full inset-x-0 max-h-[55vh] overflow-y-auto bg-white rounded-t-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.12)] py-2">
                    {steps.map((step, idx) => {
                        const stepNumber = idx + 1;
                        const isActive = stepNumber === activeStep;
                        return (
                            <li key={step.id}>
                                <button
                                    type="button"
                                    onClick={() => jumpToStep(stepNumber)}
                                    aria-current={isActive ? "step" : undefined}
                                    className={Helper.classNames(
                                        "w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer border-l-4",
                                        isActive
                                            ? "bg-orange-50 border-plot-orange"
                                            : "border-transparent hover:bg-gray-50",
                                        stepNumber < activeStep && "opacity-50"
                                    )}
                                >
                                    <span
                                        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full text-white text-sm"
                                        style={{ backgroundColor: step.color }}
                                    >
                                        <WaypointManeuverIcon
                                            waypointStep={step}
                                            stepIndex={stepNumber}
                                            numberFallback
                                        />
                                    </span>
                                    <span className="flex-1 min-w-0 text-plot-slate">
                                        {step.instruction || `Step ${stepNumber}`}
                                    </span>
                                    {step.pictureUrl && (
                                        <img
                                            src={step.pictureUrl}
                                            alt=""
                                            className="w-12 h-12 shrink-0 rounded-md object-cover"
                                        />
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ol>
            )}

            <div
                className={Helper.classNames(
                    "bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3",
                    stepListOpen ? "border-t border-gray-200" : "rounded-t-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.12)]"
                )}
            >
                <button
                    type="button"
                    aria-label="Exit directions"
                    onClick={exit}
                    className="cursor-pointer w-12 h-12 shrink-0 flex items-center justify-center rounded-full border border-gray-200 text-red-600 text-xl hover:bg-red-50"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                <div className="flex-1 min-w-0 text-center">
                    <div className="text-2xl font-bold text-plot-slate tabular-nums leading-tight">
                        {convertDistanceToReadable(remainingMeters)}
                    </div>
                    <div className="text-sm text-gray-500">
                        {stepsLeft} {stepsLeft === 1 ? "step" : "steps"} left
                    </div>
                </div>

                {hasUserLocation ? (
                    <button
                        type="button"
                        aria-label="Stop using my location"
                        title="Stop using my location"
                        onClick={toggleLocation}
                        className="cursor-pointer w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-plot-blue text-white text-lg shadow-md hover:bg-blue-600"
                    >
                        <FontAwesomeIcon icon={faLocationArrow} />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={toggleLocation}
                        className="cursor-pointer h-12 shrink-0 flex items-center gap-2 rounded-full bg-plot-blue hover:bg-blue-600 text-white font-semibold px-5"
                    >
                        <FontAwesomeIcon icon={faLocationArrow} />
                        Start
                    </button>
                )}

                <button
                    type="button"
                    aria-label={stepListOpen ? "Hide all steps" : "Show all steps"}
                    aria-expanded={stepListOpen}
                    onClick={() => setStepListOpen((open) => !open)}
                    className={Helper.classNames(
                        "cursor-pointer w-12 h-12 shrink-0 flex items-center justify-center rounded-full border text-lg",
                        stepListOpen
                            ? "bg-orange-50 border-plot-orange text-plot-orange"
                            : "border-gray-200 text-plot-slate hover:bg-gray-50"
                    )}
                >
                    <FontAwesomeIcon icon={faListUl} />
                </button>
            </div>
        </div>
    );
}
